import { create } from 'zustand';
import {
  getUnits,
  getUnit,
  createUnit,
  updateUnit,
  deleteUnit,
} from '../services/unit.service';
import { UnitSchema, UnitRequest, UpdateUnitRequest } from '../types/unit.type';

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

interface UnitState {
  units: UnitSchema[];
  selectedUnit: UnitSchema | null;
  isLoading: boolean;
  error: string | null;
  getAllUnits: () => Promise<UnitSchema[]>;
  getUnitById: (unitId: string) => Promise<UnitSchema | null>;
  createNewUnit: (data: UnitRequest) => Promise<UnitSchema | null>;
  updateUnitById: (unitId: string, data: UpdateUnitRequest) => Promise<UnitSchema | null>;
  removeUnit: (unitId: string) => Promise<boolean>;
  setSelectedUnit: (unit: UnitSchema | null) => void;
}

export const useUnitStore = create<UnitState>((set) => ({
  units: [],
  selectedUnit: null,
  isLoading: false,
  error: null,

  getAllUnits: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await getUnits();
      // Handle different response structures
      let units: UnitSchema[] = [];
      if (Array.isArray(response)) {
        units = response;
      } else if ((response as GenericResponse<UnitSchema[]>).result) {
        units = (response as GenericResponse<UnitSchema[]>).result;
      } else if ((response as any).data) {
        units = (response as any).data;
      }

      set({ isLoading: false, units });
      return units;
    } catch (error: any) {
      set({
        error: error.response?.data?.['detail'] || 'Failed to get units',
        isLoading: false,
      });
      return [];
    }
  },

  getUnitById: async (unitId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getUnit(unitId);
      let unit: UnitSchema | null = null;
      if ((response as GenericResponse<UnitSchema>).result) {
        unit = (response as GenericResponse<UnitSchema>).result;
      } else if ((response as any).data) {
        unit = (response as any).data;
      } else if ((response as any).id) {
        unit = response as UnitSchema;
      }

      set({ isLoading: false, selectedUnit: unit });
      return unit;
    } catch (error: any) {
      set({
        error: error.response?.data?.['detail'] || 'Failed to get unit',
        isLoading: false,
      });
      return null;
    }
  },

  createNewUnit: async (data: UnitRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createUnit(data);
      const responseData = response as GenericResponse<UnitSchema>;
      set({ isLoading: false });
      return responseData.result;
    } catch (error: any) {
      set({
        error: error.response?.data?.['detail'] || 'Failed to create unit',
        isLoading: false,
      });
      return null;
    }
  },

  updateUnitById: async (unitId: string, data: UpdateUnitRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateUnit(unitId, data);
      const responseData = response as GenericResponse<UnitSchema>;
      set({ isLoading: false });
      return responseData.result;
    } catch (error: any) {
      set({
        error: error.response?.data?.['detail'] || 'Failed to update unit',
        isLoading: false,
      });
      return null;
    }
  },

  removeUnit: async (unitId: string) => {
    set({ isLoading: true, error: null });
    try {
      await deleteUnit(unitId);
      set({ isLoading: false });
      return true;
    } catch (error: any) {
      set({
        error: error.response?.data?.['detail'] || 'Failed to delete unit',
        isLoading: false,
      });
      return false;
    }
  },

  setSelectedUnit: (unit: UnitSchema | null) => {
    set({ selectedUnit: unit });
  },
}));
